import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { TemplatesPageView } from './TemplatesPageView';
import { useTemplatesPage } from './useTemplatesPage';

/**
 * `/accounting/operations/templates` — the operation catalog.
 *
 * Owns its data hook and an `ErrorBoundary`; the throw lives in the inner
 * Content child because React boundaries don't catch errors thrown by the
 * component that renders them (the repo's self-served page pattern).
 *
 * The boundary earns its place here for a quieter reason than the trial
 * balance's. A failed catalog read has no wrong-but-plausible rendering to fall
 * into — there are no figures — but it does have a *silent* one: an empty table
 * under the heading "Operations", which reads as "this tenant has no operations
 * configured" and is exactly the wrong thing to tell someone whose ledger is
 * simply unreachable. So a failed read throws, and the fallback offers a retry.
 */
export const TemplatesPage: React.FC = () => {
    const page = useTemplatesPage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('operations.error.title'),
            errorMessage: t('operations.error.message'),
            errorRetry: t('operations.error.retry'),
        }),
        [t],
    );

    return (
        <div
            className="flex w-full flex-col gap-6"
            {...getNodeTestIdProp('operations-templates-page')}
        >
            <ErrorBoundary
                data-testid="operations-templates-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="operations-templates-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <TemplatesPageContent page={page} />
            </ErrorBoundary>
        </div>
    );
};

type TemplatesPageContentProps = {
    page: ReturnType<typeof useTemplatesPage>;
};

const TemplatesPageContent: React.FC<TemplatesPageContentProps> = ({
    page,
}) => {
    if (page.error) {
        throw page.error;
    }

    // The View keeps its toolbar and footer notes while loading and skeletons
    // only the rows — the repo's loading-state standard.
    return (
        <TemplatesPageView
            access={page.access}
            canEdit={page.canEdit}
            canRead={page.canRead}
            emptyState={page.emptyState}
            historyKey={page.historyKey}
            isLoading={page.isLoading}
            locale={page.locale}
            onCloseHistory={page.onCloseHistory}
            onCreate={page.onCreate}
            onEdit={page.onEdit}
            onManageCategories={page.onManageCategories}
            onOpenHistory={page.onOpenHistory}
            onPreview={page.onPreview}
            onPreviewOpenChange={page.onPreviewOpenChange}
            onSearchChange={page.onSearchChange}
            onSetBlocked={page.onSetBlocked}
            onStatusFilterChange={page.onStatusFilterChange}
            previewRow={page.previewRow}
            rows={page.rows ?? []}
            search={page.search}
            statusFilter={page.statusFilter}
            totalCount={page.totalCount}
            visibleCount={page.rows?.length ?? 0}
        />
    );
};
